// C:\Users\avnis\CareCo-OrdinatorDashboard\client\src\CareCoordinator\components\graphs\enhancedgraph\heartrate.jsx
//
// Live heart-rate chart that scrolls continuously like a stock (Nifty) chart.
//
// - New values enter at the right edge and the whole trace flows left.
// - Y axis auto-scales to the visible window, with labelled gridlines.
// - Realistic variability between server readings (drift, jitter, breathing).
// - Colour follows the heart-rate zone, as a gradient on the line and fill:
//     Normal (50-100)  -> green
//     Medium (100-120) -> orange
//     High   (>120)    -> red
//     Low    (<50)     -> orange (labelled "Low")
//   Change the thresholds with LOW_BPM / MED_BPM / HIGH_BPM below.
// - When there is no live data (no reading yet, stream stale, reconnecting,
//   error, tab hidden) the readout shows "Null" and the chart stops moving
//   (frozen, slightly dimmed). It never invents values.
// - The current BPM is shown in the right-side panel.

import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { openHeartRateStream, extractHeartRateValue, extractHeartRateTimestamp } from "../../../data/enhancemonitoring";

const BASELINE_BPM = 60;
const MIN_BPM = 30;
const MAX_BPM = 180;

// Zone thresholds
const LOW_BPM = 50; // below this -> orange, "Low"
const MED_BPM = 100; // above this -> orange, "Medium"
const HIGH_BPM = 120; // above this -> red, "High"

const PX_PER_SECOND = 55; // scroll speed of the chart
const EASE_TAU = 0.5; // seconds; how quickly the line eases toward a new reading
const MIN_AXIS_SPAN = 20; // bpm; the Y axis never zooms in tighter than this
const PANEL_MAX_PX = 190; // right-side readout panel width
const RESP_PERIOD = 5; // seconds; gentle "breathing" wave

const GRID_MAJOR = "rgba(255, 255, 255, 0.075)";
const GRID_MINOR = "rgba(255, 255, 255, 0.03)";
const BG_TOP = "#0e141b";
const BG_BOTTOM = "#080b0f";
const BG_BOTTOM_RGB = "8, 11, 15";
const SCREEN_BG = "#0a0e12";
const STALE_AFTER_MS = 4000; // backend pushes ~1 reading/sec, so 4s of silence is a genuine gap

// ── zone colours ─────────────────────────────────────────────────────────
const GREEN = [34, 197, 94];
const ORANGE = [245, 158, 11];
const RED = [255, 77, 79];

// bpm -> colour stops, highest first. Each boundary blends over ~8 bpm so
// the line shifts colour smoothly instead of switching abruptly.
const BAND_STOPS = [
  [HIGH_BPM + 4, RED],
  [HIGH_BPM - 4, ORANGE],
  [MED_BPM + 4, ORANGE],
  [MED_BPM - 4, GREEN],
  [LOW_BPM + 4, GREEN],
  [LOW_BPM - 4, ORANGE],
];

const rgba = (c, a) => `rgba(${Math.round(c[0])}, ${Math.round(c[1])}, ${Math.round(c[2])}, ${a})`;

function zoneRGB(v) {
  if (v >= BAND_STOPS[0][0]) return BAND_STOPS[0][1];
  for (let i = 1; i < BAND_STOPS.length; i++) {
    const [hiV, hiC] = BAND_STOPS[i - 1];
    const [loV, loC] = BAND_STOPS[i];
    if (v >= loV) {
      const f = (hiV - v) / (hiV - loV);
      return [0, 1, 2].map((j) => hiC[j] + (loC[j] - hiC[j]) * f);
    }
  }
  return BAND_STOPS[BAND_STOPS.length - 1][1];
}

function zoneOf(bpm) {
  if (bpm > HIGH_BPM) return { label: "High", color: "#ff4d4f", glow: "rgba(255, 77, 79, 0.55)" };
  if (bpm > MED_BPM) return { label: "Medium", color: "#f59e0b", glow: "rgba(245, 158, 11, 0.55)" };
  if (bpm < LOW_BPM) return { label: "Low", color: "#f59e0b", glow: "rgba(245, 158, 11, 0.55)" };
  return { label: "Normal", color: "#22c55e", glow: "rgba(34, 197, 94, 0.55)" };
}

// Gradient laid out in bpm-space: the same bpm always gets the same colour,
// wherever it sits on screen.
function bandGradient(ctx, yTop, yBottom, alpha) {
  const g = ctx.createLinearGradient(0, yTop, 0, yBottom);
  for (const [bpm, c] of BAND_STOPS) {
    g.addColorStop((MAX_BPM - bpm) / (MAX_BPM - MIN_BPM), rgba(c, alpha));
  }
  return g;
}

// ── helpers ──────────────────────────────────────────────────────────────
function randn() {
  let u = 0;
  while (u === 0) u = Math.random();
  const v = Math.random();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

function niceStep(span, target = 5) {
  const raw = Math.max(span, 1e-6) / target;
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  const f = raw / pow;
  const nf = f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10;
  return Math.max(1, nf * pow);
}

function HeartGlyph({ size = 16, className, style }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" className={className} style={style}>
      <path d="M12 21s-6.7-4.35-9.33-8.06C.86 10.4 1.2 6.9 3.8 5.1c2.2-1.53 5-.98 6.6 1.02.5.62.9 1.3 1.6 1.3.7 0 1.1-.68 1.6-1.3 1.6-2 4.4-2.55 6.6-1.02 2.6 1.8 2.94 5.3 1.13 7.84C18.7 16.65 12 21 12 21Z" />
    </svg>
  );
}

function ExpandIcon({ size = 15 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}

function CollapseIcon({ size = 15 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="4 14 10 14 10 20" />
      <polyline points="20 10 14 10 14 4" />
      <line x1="14" y1="10" x2="21" y2="3" />
      <line x1="10" y1="14" x2="3" y2="21" />
    </svg>
  );
}

function statusMeta(status) {
  switch (status) {
    case "live":
      return { label: "Live", color: "#22c55e" };
    case "stale":
      return { label: "No live signal", color: "#eab308" };
    case "paused":
      return { label: "Paused (tab hidden)", color: "#64748b" };
    case "connecting":
      return { label: "Connecting…", color: "#eab308" };
    case "reconnecting":
      return { label: "Reconnecting…", color: "#eab308" };
    case "error":
      return { label: "Connection lost", color: "#ef4444" };
    default:
      return { label: "Idle", color: "#64748b" };
  }
}

export default function HeartRateMonitor({ patient, height = 260 }) {
  const ownerId = patient?.owner_id;

  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const readoutRef = useRef(null);

  // Mutable animation state lives in refs so the RAF loop never fights React re-renders.
  const signalRef = useRef(false); // true only while live data is actually flowing
  const hasDataRef = useRef(false);
  const displayValueRef = useRef(BASELINE_BPM);
  const targetValueRef = useRef(BASELINE_BPM);
  const noiseRef = useRef({ slow: 0, fast: 0, phase: Math.random() * Math.PI * 2 });
  const axisRef = useRef({ lo: BASELINE_BPM - 15, hi: BASELINE_BPM + 15 });
  const bufferRef = useRef(new Float32Array(0)); // one BPM sample per device pixel column
  const scrollAccRef = useRef(0);
  const scrollTotalRef = useRef(0);
  const lastFrameTimeRef = useRef(null);
  const rafRef = useRef(null);
  const dprRef = useRef(1);
  const beatPulseRef = useRef(0);
  const lastMessageAtRef = useRef(null);
  const lastBpmRef = useRef(null);

  const [status, setStatus] = useState("connecting");
  const [currentBpm, setCurrentBpm] = useState(null);
  const [delta, setDelta] = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(
    typeof document === "undefined" ? true : document.visibilityState === "visible"
  );

  // Live = connection is up AND we have actually received a reading.
  const hasSignal = status === "live" && currentBpm != null;
  useEffect(() => {
    signalRef.current = hasSignal;
  }, [hasSignal]);

  // ── Only keep the stream open while the browser tab is visible. ──
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(document.visibilityState === "visible");
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // ── Canvas sizing ──
  // The buffer holds BPM values (not pixel coordinates) and is aligned to the
  // right edge, so resizing or toggling fullscreen keeps the history intact.
  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    dprRef.current = dpr;

    const cssWidth = container.clientWidth;
    const cssHeight = container.clientHeight || height;

    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;
    canvas.width = Math.max(1, Math.round(cssWidth * dpr));
    canvas.height = Math.max(1, Math.round(cssHeight * dpr));

    const panelCss = Math.min(PANEL_MAX_PX, cssWidth * 0.3);
    const plotWidth = Math.max(1, Math.round((cssWidth - panelCss) * dpr));

    const next = new Float32Array(plotWidth).fill(NaN);
    const prev = bufferRef.current;
    const keep = Math.min(prev.length, next.length);
    if (keep > 0) next.set(prev.subarray(prev.length - keep), next.length - keep);
    bufferRef.current = next;
  }, [height]);

  useEffect(() => {
    setupCanvas();
    const ro = new ResizeObserver(() => setupCanvas());
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [setupCanvas]);

  useLayoutEffect(() => {
    setupCanvas();
  }, [isFullscreen, setupCanvas]);

  // ── Fullscreen: lock page scroll, Escape closes ──
  useEffect(() => {
    if (!isFullscreen) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (e) => {
      if (e.key === "Escape") setIsFullscreen(false);
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFullscreen]);

  // ── Start fresh when the patient changes ──
  useEffect(() => {
    hasDataRef.current = false;
    lastBpmRef.current = null;
    lastMessageAtRef.current = null;
    bufferRef.current.fill(NaN);
    setCurrentBpm(null);
    setDelta(0);
    setLastUpdatedAt(null);
  }, [ownerId]);

  // ── SSE stream ──
  useEffect(() => {
    if (!ownerId) {
      setStatus("idle");
      return undefined;
    }
    if (!isPageVisible) {
      setStatus("paused");
      return undefined;
    }

    setStatus("connecting");
    const stream = openHeartRateStream({
      ownerId,
      onOpen: () => setStatus("live"),
      onData: (payload) => {
        const value = extractHeartRateValue(payload);
        if (value == null) return;
        const clamped = Math.max(MIN_BPM, Math.min(MAX_BPM, value));
        const serverTs = extractHeartRateTimestamp(payload);

        if (!hasDataRef.current) {
          hasDataRef.current = true;
          displayValueRef.current = clamped;
          axisRef.current = { lo: clamped - 10, hi: clamped + 10 };
        }
        targetValueRef.current = clamped;
        beatPulseRef.current = 1;
        lastMessageAtRef.current = Date.now();

        const rounded = Math.round(clamped);
        setDelta(lastBpmRef.current == null ? 0 : rounded - lastBpmRef.current);
        lastBpmRef.current = rounded;
        setCurrentBpm(rounded);
        setLastUpdatedAt(serverTs != null ? new Date(serverTs) : new Date());
        setStatus("live");
      },
      onError: () => setStatus("error"),
      onClose: () => setStatus("reconnecting"),
    });

    return () => stream.close();
  }, [ownerId, isPageVisible]);

  // ── Staleness watchdog: no reading for STALE_AFTER_MS -> "stale" -> Null + frozen chart ──
  useEffect(() => {
    const interval = setInterval(() => {
      setStatus((prev) => {
        if (prev !== "live" && prev !== "stale") return prev;
        const last = lastMessageAtRef.current;
        const isStale = last != null && Date.now() - last > STALE_AFTER_MS;
        return isStale ? "stale" : "live";
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // ── Draw loop ──
  useEffect(() => {
    function nextSample(dtCol) {
      if (!hasDataRef.current) return NaN;

      const diff = targetValueRef.current - displayValueRef.current;
      displayValueRef.current += diff * (1 - Math.exp(-dtCol / EASE_TAU));

      // While the reading is moving, the line gets more "alive".
      const vol = 1 + Math.min(3, Math.abs(diff) * 0.15);

      const n = noiseRef.current;
      n.slow += (-n.slow / 0.9) * dtCol + 1.5 * vol * Math.sqrt(dtCol) * randn(); // drift
      n.fast += (-n.fast / 0.12) * dtCol + 2.2 * vol * Math.sqrt(dtCol) * randn(); // jitter
      n.phase += (dtCol * 2 * Math.PI) / RESP_PERIOD;
      const breathing = Math.sin(n.phase) * 0.9;

      const v = displayValueRef.current + n.slow + n.fast + breathing;
      return Math.max(MIN_BPM, Math.min(MAX_BPM, v));
    }

    function frame(now) {
      rafRef.current = requestAnimationFrame(frame);

      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      const buffer = bufferRef.current;
      if (!canvas || !ctx || buffer.length === 0) return;

      const last = lastFrameTimeRef.current ?? now;
      const dt = Math.min(0.1, (now - last) / 1000);
      lastFrameTimeRef.current = now;

      const live = signalRef.current;
      const dtMove = live ? dt : 0; // no live data -> nothing moves

      const dpr = dprRef.current;
      const w = canvas.width;
      const h = canvas.height;
      const N = buffer.length; // plot width in device px; the panel sits to the right of it
      const pxPerSec = PX_PER_SECOND * dpr;

      // ── Scroll: shift history left, append new samples on the right ──
      scrollAccRef.current += pxPerSec * dtMove;
      const cols = Math.floor(scrollAccRef.current);
      if (cols > 0) {
        scrollAccRef.current -= cols;
        scrollTotalRef.current += cols;
        const n = Math.min(cols, N);
        buffer.copyWithin(0, n);
        const colDt = 1 / pxPerSec;
        for (let k = N - n; k < N; k++) buffer[k] = nextSample(colDt);
      }
      const subPx = scrollAccRef.current;
      const gridScroll = scrollTotalRef.current + subPx;

      beatPulseRef.current = Math.max(0, beatPulseRef.current - dt * 1.6);

      // ── Auto-scaling Y axis ──
      let lo = Infinity;
      let hi = -Infinity;
      for (let i = 0; i < N; i++) {
        const v = buffer[i];
        if (v === v) {
          if (v < lo) lo = v;
          if (v > hi) hi = v;
        }
      }
      const hasPoints = lo !== Infinity;
      if (!hasPoints) {
        lo = BASELINE_BPM - 5;
        hi = BASELINE_BPM + 5;
      }
      const mid = (lo + hi) / 2;
      const half = Math.max(((hi - lo) / 2) * 1.35, MIN_AXIS_SPAN / 2);
      const axis = axisRef.current;
      const k = 1 - Math.exp(-dt * 3);
      axis.lo += (mid - half - axis.lo) * k;
      axis.hi += (mid + half - axis.hi) * k;

      const top = 34 * dpr;
      const bottom = h - 14 * dpr;
      const span = axis.hi - axis.lo;
      const yOf = (v) => top + ((axis.hi - v) / span) * (bottom - top);
      const valuePerPx = span / (bottom - top);

      const zc = zoneRGB(displayValueRef.current);
      const glow = rgba(zc, 0.55);
      const chartAlpha = live ? 1 : 0.45;

      // ── Background ──
      ctx.clearRect(0, 0, w, h);
      const bg = ctx.createLinearGradient(0, 0, 0, h);
      bg.addColorStop(0, BG_TOP);
      bg.addColorStop(1, BG_BOTTOM);
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      // Path of the trace; NaN samples break the line (nothing drawn before data starts).
      const trace = () => {
        ctx.beginPath();
        let started = false;
        let count = 0;
        let firstX = 0;
        let lastX = 0;
        for (let i = 0; i < N; i++) {
          const v = buffer[i];
          if (v !== v) {
            started = false;
            continue;
          }
          const x = i - subPx;
          const y = yOf(v);
          if (!started) {
            ctx.moveTo(x, y);
            started = true;
          } else {
            ctx.lineTo(x, y);
          }
          if (count === 0) firstX = x;
          lastX = x;
          count++;
        }
        return { count, firstX, lastX };
      };

      const yBandTop = yOf(MAX_BPM);
      const yBandBottom = yOf(MIN_BPM);

      // ── Gradient area under the line (zone colours, fading into the background) ──
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, N, h);
      ctx.clip();
      ctx.globalAlpha = chartAlpha;
      const t = trace();
      if (t.count > 1) {
        ctx.lineTo(t.lastX, h);
        ctx.lineTo(t.firstX, h);
        ctx.closePath();
        ctx.save();
        ctx.clip();
        ctx.fillStyle = bandGradient(ctx, yBandTop, yBandBottom, 0.34);
        ctx.fillRect(0, 0, N, h);
        const fade = ctx.createLinearGradient(0, yOf(hi), 0, h);
        fade.addColorStop(0, `rgba(${BG_BOTTOM_RGB}, 0)`);
        fade.addColorStop(1, `rgba(${BG_BOTTOM_RGB}, 0.96)`);
        ctx.fillStyle = fade;
        ctx.fillRect(0, 0, N, h);
        ctx.restore();
      }
      ctx.restore();

      // ── Full-screen grid (scrolls with the trace) ──
      ctx.lineWidth = 1;
      const majorX = 60 * dpr;
      const minorX = majorX / 5;
      const tickStep = niceStep(span, 5);
      const minorStep = tickStep / 5;
      const vLo = axis.lo - (h - bottom) * valuePerPx;
      const vHi = axis.hi + top * valuePerPx;

      ctx.strokeStyle = GRID_MINOR;
      ctx.beginPath();
      for (let x = -(gridScroll % minorX); x < w; x += minorX) {
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
      }
      for (let v = Math.ceil(vLo / minorStep) * minorStep; v <= vHi; v += minorStep) {
        const y = yOf(v);
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
      }
      ctx.stroke();

      ctx.strokeStyle = GRID_MAJOR;
      ctx.beginPath();
      for (let x = -(gridScroll % majorX); x < w; x += majorX) {
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
      }
      for (let v = Math.ceil(vLo / tickStep) * tickStep; v <= vHi; v += tickStep) {
        const y = yOf(v);
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
      }
      ctx.stroke();

      // ── Line: zone-coloured gradient (green / orange / red by bpm) ──
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, N, h);
      ctx.clip();
      ctx.globalAlpha = chartAlpha;
      const t2 = trace();
      if (t2.count > 1) {
        ctx.lineWidth = 2 * dpr;
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        ctx.strokeStyle = bandGradient(ctx, yBandTop, yBandBottom, 1);
        if (live) {
          ctx.shadowColor = glow;
          ctx.shadowBlur = 10 * dpr;
        }
        ctx.stroke();
        ctx.shadowBlur = 0;
      }
      ctx.restore();

      // ── Readout panel backdrop (grid stays visible through it) ──
      ctx.fillStyle = "rgba(0, 0, 0, 0.30)";
      ctx.fillRect(N, 0, w - N, h);
      ctx.strokeStyle = "rgba(255, 255, 255, 0.10)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(N + 0.5, 0);
      ctx.lineTo(N + 0.5, h);
      ctx.stroke();

      // ── Axis labels ──
      ctx.font = `${10 * dpr}px system-ui, -apple-system, "Segoe UI", sans-serif`;
      ctx.fillStyle = "rgba(255, 255, 255, 0.38)";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      for (let v = Math.ceil(axis.lo / tickStep) * tickStep; v <= axis.hi; v += tickStep) {
        const y = yOf(v);
        if (y < top - 4 * dpr || y > bottom + 4 * dpr) continue;
        ctx.fillText(String(Math.round(v)), N + 8 * dpr, y);
      }

      // ── Head dot + pulse ring (only while live) ──
      const headV = buffer[N - 1];
      if (live && headV === headV) {
        const hx = N - 1 - subPx;
        const hy = yOf(headV);
        const pulse = beatPulseRef.current;
        if (pulse > 0.01) {
          ctx.beginPath();
          ctx.strokeStyle = rgba(zc, 0.55 * pulse);
          ctx.lineWidth = 1.5 * dpr;
          ctx.arc(hx, hy, (4 + (1 - pulse) * 9) * dpr, 0, Math.PI * 2);
          ctx.stroke();
        }
        ctx.beginPath();
        ctx.fillStyle = "#fff";
        ctx.shadowColor = glow;
        ctx.shadowBlur = 10 * dpr;
        ctx.arc(hx, hy, 3.2 * dpr, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      // Pulse the readout in time with each fresh reading.
      if (readoutRef.current) {
        const p = live ? beatPulseRef.current : 0;
        readoutRef.current.style.transform = `scale(${1 + p * 0.1})`;
      }
    }

    rafRef.current = requestAnimationFrame(frame);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      lastFrameTimeRef.current = null;
    };
  }, []);

  const meta = statusMeta(status);
  const zone = hasSignal ? zoneOf(currentBpm) : null;
  const numberSize = isFullscreen ? 64 : 44;

  return (
    <div
      ref={containerRef}
      style={
        isFullscreen
          ? {
              position: "fixed",
              inset: 0,
              width: "100vw",
              height: "100vh",
              borderRadius: 0,
              overflow: "hidden",
              background: SCREEN_BG,
              border: "none",
              boxShadow: "inset 0 0 60px rgba(0,0,0,0.6)",
              zIndex: 1000,
            }
          : {
              position: "relative",
              width: "100%",
              height,
              borderRadius: 10,
              overflow: "hidden",
              background: SCREEN_BG,
              border: "1px solid rgba(255,255,255,0.06)",
              boxShadow: "inset 0 0 40px rgba(0,0,0,0.5)",
            }
      }
    >
      <canvas ref={canvasRef} style={{ display: "block" }} />

      {/* Fullscreen toggle */}
      <button
        type="button"
        onClick={() => setIsFullscreen((v) => !v)}
        title={isFullscreen ? "Exit fullscreen (Esc)" : "View fullscreen"}
        style={{
          position: "absolute",
          top: 10,
          right: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 28,
          height: 28,
          borderRadius: 6,
          border: "1px solid rgba(255,255,255,0.12)",
          background: "rgba(255,255,255,0.06)",
          color: "rgba(255,255,255,0.75)",
          cursor: "pointer",
        }}
      >
        {isFullscreen ? <CollapseIcon size={14} /> : <ExpandIcon size={14} />}
      </button>

      {/* Status pill */}
      <div
        style={{
          position: "absolute",
          top: 10,
          left: 12,
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 11,
          fontWeight: 500,
          color: "rgba(255,255,255,0.7)",
          letterSpacing: 0.2,
        }}
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: meta.color,
            boxShadow: status === "live" ? `0 0 6px ${meta.color}` : "none",
          }}
        />
        {meta.label}
      </div>

      {/* Right-side readout */}
      <div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          right: 0,
          width: `min(${PANEL_MAX_PX}px, 30%)`,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          justifyContent: "center",
          paddingRight: 16,
          boxSizing: "border-box",
          pointerEvents: "none",
        }}
      >
        <div ref={readoutRef} style={{ transformOrigin: "right center", textAlign: "right" }}>
          {hasSignal ? (
            <>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "flex-end", gap: 8 }}>
                <HeartGlyph
                  size={isFullscreen ? 26 : 20}
                  style={{
                    color: zone.color,
                    filter: `drop-shadow(0 0 4px ${zone.glow})`,
                    alignSelf: "center",
                    transition: "color 400ms ease",
                  }}
                />
                <span
                  style={{
                    fontSize: numberSize,
                    lineHeight: 1,
                    fontWeight: 600,
                    color: "#fff",
                    textShadow: `0 0 12px ${zone.glow}`,
                    fontVariantNumeric: "tabular-nums",
                    transition: "text-shadow 400ms ease",
                  }}
                >
                  {currentBpm}
                </span>
              </div>
              <div
                style={{
                  marginTop: 4,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                  fontSize: 12,
                  color: "rgba(255,255,255,0.55)",
                }}
              >
                <span>bpm</span>
                <span style={{ color: zone.color, fontWeight: 600, transition: "color 400ms ease" }}>{zone.label}</span>
                {delta !== 0 && (
                  <span style={{ fontVariantNumeric: "tabular-nums", color: "rgba(255,255,255,0.7)" }}>
                    {delta > 0 ? "▲" : "▼"} {Math.abs(delta)}
                  </span>
                )}
              </div>
              {lastUpdatedAt && (
                <div style={{ marginTop: 6, fontSize: 10, color: "rgba(255,255,255,0.35)" }}>
                  updated {lastUpdatedAt.toLocaleTimeString()}
                </div>
              )}
            </>
          ) : (
            <span
              style={{
                fontSize: numberSize * 0.6,
                lineHeight: 1,
                fontWeight: 600,
                color: "rgba(255,255,255,0.4)",
                letterSpacing: 0.5,
              }}
            >
              Null
            </span>
          )}
        </div>
      </div>
    </div>
  );
}